// Cook-step timer family extracted from CookMode.jsx 2026-05-03.
//
// Timer            — single wall-clock-driven countdown (chimes on done).
// MultiStepTimer   — sequential cascade of named stages (multi-step recipes).
// TimerStack       — tabbed stack of multiple parallel timers.
// ManualTimer      — paused-by-default, user-driven start/pause/reset.
// timerActionBtnStyle — shared button style helper for the action rows.
//
// All four are wall-clock-driven, not interval-decremented, so they survive
// background throttling on locked phones and stay in sync with server-side

// push-notification timers (the cook_step_notifications cron drain).










import { useState, useEffect, useRef } from "react";
import { COLOR, FONT } from "../lib/tokens";
import { playTimerChime, stopTimerChime } from "../lib/cookAudio";
import { supabase } from "../lib/supabase";

// NOTE — Live Activity orchestration moved to CookMode 2026-05-08.
// Timer / ManualTimer used to call BoodaTimer.start/update/end
// directly; the activity is now session-scoped (one activity for the
// whole cook), so CookMode listens for these timer-state callbacks
// and dispatches a single BoodaTimer.updateSession with the active
// timer's fields. Timer / ManualTimer stay agnostic about the lock-
// screen surface — they just announce what their state is doing.

// Tiered warning threshold — when (in seconds-remaining) to fire the
// onWarning callback so the cuisine voiceline can play. Fixed 30s
// makes sense for a quick sauté but vanishes inside a brisket — by
// the time "thirty seconds" plays, the cook is missing the drop
// window. Tiers snap to numbers a cook actually parses off a clock
// ("two minutes", "five minutes", "fifteen minutes") — not an
// awkward percentage like 12.5% of 4 hours.
//
// Returns null when the total timer is too short to meaningfully warn
// — the chime IS the warning at that point.
function warningSecondsFor(totalSeconds) {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 30) return null;
  if (totalSeconds <= 5  * 60)        return 30;             // <= 5m  → 30s
  if (totalSeconds <= 30 * 60)        return 2  * 60;        // <= 30m → 2m
  if (totalSeconds <= 90 * 60)        return 5  * 60;        // <= 90m → 5m
  if (totalSeconds <= 6  * 60 * 60)   return 15 * 60;        // <= 6h  → 15m
  return 30 * 60;                                            // overnight → 30m
}

export function Timer({
  seconds, onDone, onStart, onWarning, endsAt,
  // initialDone — hydrate the Timer in its DONE state. Used when
  // the user re-opens a step whose timer has already rung (DB has
  // timer_state.ended === true). Without this, Timer initializes
  // to done=false and shows "4:00 START" as if the step were
  // fresh, contradicting the home banner's "0:00 done" display.
  initialDone = false,
  // Timer-state callbacks. Each fires when the in-app timer enters
  // the named state with the relevant detail; CookMode wires these
  // into BoodaTimer.updateSession so the lock-screen activity
  // matches what the cook sees in the app.
  //   onTimerStart(deadlineMs)        — fresh start or banner resume
  //   onTimerPause(remainingMs)
  //   onTimerResume(newDeadlineMs)
  //   onTimerEnd()                    — timer reached zero naturally
  //   onTimerReset()                  — user tapped reset (returns to nominal)
  onTimerStart, onTimerPause, onTimerResume, onTimerEnd, onTimerReset,
}) {
  // Wall-clock-driven countdown.
  //
  // The old implementation decremented `remaining` every setTimeout(1s),
  // which sounded fine but drifted badly whenever the browser throttled
  // JS — phone locks, tab backgrounds for long stretches, OS battery
  // saver. On unlock the countdown would resume from its paused value
  // instead of catching up to real wall-clock elapsed, leaving the
  // in-app timer wildly out of sync with the server-side push.
  //
  // Now we pin an absolute `deadline` once, then every render-interval
  // compute remaining = ceil((deadline - Date.now())/1000). Throttled
  // browsers will just re-render late; the MATH always reads from the
  // wall clock, so an unlock snaps the UI to the correct remaining in
  // one paint. The push in cook_step_notifications fires off the same
  // absolute instant, so the two surfaces stay aligned regardless of
  // what JS was doing in the meantime.
  //
  // `endsAt` (from the banner resume handoff) wins over `seconds`
  // because it's the real server-side deadline. Fresh starts use
  // seconds → now + seconds*1000.
  const initialDeadline = endsAt
    ? new Date(endsAt).getTime()
    : Date.now() + seconds * 1000;
  // Manual-start contract: the timer doesn't begin counting until
  // the user explicitly taps START — same as TimerStack stages. The
  // ONE exception is a banner resume: when endsAt is provided AND
  // still in the future, the cook is mid-step from a previous
  // session and we keep the wall-clock deadline (auto-started).
  const initialStarted = (() => {
    // initialDone takes precedence — a done timer is conceptually
    // "started and finished," so started=true so the DONE-state UI
    // (✓ + RESTART) renders correctly.
    if (initialDone) return true;
    if (!endsAt) return false;
    const ms = new Date(endsAt).getTime();
    return Number.isFinite(ms) && ms > Date.now();
  })();
  const [started, setStarted]      = useState(initialStarted);
  const [deadline, setDeadline]    = useState(initialDeadline);
  const [pausedAt, setPausedAt]    = useState(null);  // ms — when running=false
  const [tick, setTick]            = useState(0);
  const [done, setDone]            = useState(initialDone);
  const running = started && pausedAt == null;

  // Mount diagnostic — see whether endsAt arrived in time for the
  // initial state to pin to the live deadline OR if we got here with
  // null (meaning we'll need the late-arriving useEffect below to
  // catch up).
  useEffect(() => {
    const remainAtMount = endsAt
      ? Math.max(0, Math.round((new Date(endsAt).getTime() - Date.now()) / 1000))
      : null;
    console.log("[Timer:mount]",
      "endsAt=", endsAt,
      "initialStarted=", initialStarted,
      "initialDeadline=", new Date(initialDeadline).toISOString(),
      "remainAtMount=", remainAtMount,
      "seconds=", seconds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Banner-resume — the timer hydrates with started=true and an
  // already-pinned deadline. Fire onTimerStart once on mount so
  // CookMode can hydrate the Live Activity's active-timer fields
  // even when the user didn't tap START in this session.
  useEffect(() => {
    if (!initialStarted) return;
    try { onTimerStart?.(initialDeadline, seconds); }
    catch (e) { console.warn("[Timer] onTimerStart threw:", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Late-arriving initialDone. Parent's `telemetry.activeStep` can
  // resolve AFTER the Timer mounts (Supabase fetch is async); when
  // it does, we need to flip into the DONE state even though
  // useState already captured initialDone=false at mount. Only
  // promotes false → true; never un-marks done (the user can
  // explicitly tap Restart for that).
  useEffect(() => {
    if (initialDone && !done) {
      setDone(true);
      setStarted(true);
    }
  }, [initialDone, done]);

  // Late-arriving endsAt resume. The Timer initial state is captured
  // ONCE on first render. When the parent passes endsAt synchronously
  // at mount we adopt it via the useState above. But CookMode's
  // endsAt prop is sometimes resolved async — telemetry.startStep
  // adopts the existing cook_session_steps row in a Promise that
  // settles AFTER the Timer has already mounted. In that case the
  // Timer was mounted with endsAt=null and is sitting on the nominal
  // duration; once the prop arrives we need to re-pin the deadline
  // and re-arm `started`. Without this, navigating step 1 → 2 → 1
  // resets the running timer back to its full nominal — the very
  // bug d47e47f tried to fix at the parent layer.
  //
  // Guards:
  //   • Skip while we already have a running deadline that matches
  //     the incoming endsAt (no-op identity).
  //   • Skip while paused — the user has explicit state; don't
  //     overwrite their paused snapshot from the prop change.
  useEffect(() => {
    if (!endsAt) return;
    if (pausedAt != null) return;
    const ms = new Date(endsAt).getTime();
    if (!Number.isFinite(ms)) return;
    if (deadline === ms && started) return;
    setDeadline(ms);
    setStarted(ms > Date.now());
    setDone(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt]);

  // While running: count down toward the deadline.
  // While paused:  freeze at the remaining time captured AT the moment
  //                of pause (deadline - pausedAt). The previous formula
  //                used (pausedAt - Date.now()) which goes negative the
  //                instant any time elapses post-pause, clamping the
  //                display to 0:00. This held the bar/digits at a
  //                stable snapshot until the user resumes.
  const remaining = !started ? seconds : Math.max(
    0,
    Math.ceil((running ? deadline - Date.now() : deadline - pausedAt) / 1000),
  );

  // MARK DONE failsafe — manual override. Fires the same path as a
  // natural rollover-to-zero. Reasons this button exists:
  //   1. Timer state didn't hydrate cleanly on resume (endsAt missing
  //      or stale) and the countdown is wedged.
  //   2. The user actually completed the step but the timer never
  //      reached 0 (oven preheated faster than expected, dough rose
  //      faster than spec, etc.).
  //   3. Network / push lag is leaving the lock-screen activity
  //      pinned on a finished step and the user wants to advance.
  // Idempotent — guard on `done` so re-tapping doesn't double-fire.
  const markDone = () => {
    if (done) return;
    try { stopTimerChime(); } catch { /* noop */ }
    setDone(true);
    setPausedAt(Date.now()); // stop the interval
    try { onDone?.(); } catch (e) { console.warn("[Timer] onDone threw:", e); }
    try { onTimerEnd?.(); } catch (e) { console.warn("[Timer] onTimerEnd threw:", e); }
  };

  // Reset → back to "not started" so the user has to explicitly
  // tap START again. Consistent with the no-auto-start principle.
  const reset = () => {
    setStarted(false);
    setDeadline(Date.now() + seconds * 1000);
    setPausedAt(null);
    setDone(false);
    // Reset is its own signal — participants snap the countdown
    // back to nominal. Previously this also fired onTimerEnd which
    // conflated "user reset" with "timer rang", clobbering the
    // participant-side state machine. Callers that need "either"
    // can wire both handlers to the same function.
    try { onTimerReset?.(); }
    catch (e) { console.warn("[Timer] onTimerReset threw:", e); }
  };

  // Restart → re-run a finished timer. Fresh deadline, done=false,
  // fires onTimerStart so CookMode clears the `ended` flag in DB
  // and drops this segment out of completedTimerIndices on the
  // bar. Distinct from reset (which goes back to "not started"
  // requiring an explicit START); restart immediately re-arms.
  const restart = () => {
    try { stopTimerChime(); } catch { /* noop */ }
    const newDeadline = Date.now() + seconds * 1000;
    setDeadline(newDeadline);
    setPausedAt(null);
    setDone(false);
    setStarted(true);
    warnedRef.current = false;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cookmode:timer-started"));
    }
    try { onStart?.(); } catch (e) { console.warn("[Timer] onStart (restart) threw:", e); }
    try { onTimerStart?.(newDeadline, seconds); }
    catch (e) { console.warn("[Timer] onTimerStart (restart) threw:", e); }
  };

  // Start the timer. Pins the deadline to NOW + seconds, flips
  // started=true, and dispatches a `cookmode:timer-started` event so
  // the per-step TTS narration can pause itself (no overlap with
  // the voiceline that's about to play).
  const startTimer = () => {
    if (started) return;
    const newDeadline = Date.now() + seconds * 1000;
    setDeadline(newDeadline);
    setPausedAt(null);
    setDone(false);
    setStarted(true);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cookmode:timer-started"));
    }
    try { onStart?.(); } catch (e) { console.warn("[Timer] onStart threw:", e); }
    try { onTimerStart?.(newDeadline, seconds); }
    catch (e) { console.warn("[Timer] onTimerStart threw:", e); }
  };

  // Banner-resume case: started auto-resolved to true on mount, so
  // we ALSO need to dispatch the timer-started event once so the
  // narration audio that auto-plays on step entry pauses itself.
  // Effect is gated on initialStarted so it only runs in the resume
  // case — fresh mounts wait for the explicit startTimer() above.
  const dispatchedRef = useRef(false);
  useEffect(() => {
    if (!started || dispatchedRef.current) return;
    dispatchedRef.current = true;
    if (initialStarted && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cookmode:timer-started"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);
  // Re-pin the deadline when `seconds` itself changes mid-cook
  // (calibration update mid-step). Skips the initial mount —
  // useState already pinned the deadline correctly (from endsAt
  // when resuming, or now+seconds for a fresh start). Without the
  // ref guard, this effect runs on first paint and CLOBBERS the
  // endsAt-derived deadline with now+seconds, resetting the timer
  // to full duration every time CookMode mounted from the banner.
  // That broke the persistent-server-timer contract — the entire
  // point of the deliver_at architecture.
  const prevSecondsRef = useRef(seconds);
  useEffect(() => {
    if (prevSecondsRef.current === seconds) return;
    prevSecondsRef.current = seconds;
    setDeadline(Date.now() + seconds * 1000);
    setPausedAt(null);
    setDone(false);
  }, [seconds]);

  // Voice-command bridge — listens for the three synthetic-tap
  // events dispatched from CookMode's voice-command handler.
  // Mirrors the hand-tap UI: START runs startTimer(), PAUSE toggles
  // pause/resume, RESET re-pins the deadline. Wrapped in stable
  // refs so the effect's listener doesn't churn on every render.
  const startedRef = useRef(started);
  const runningRef = useRef(running);
  const doneRef    = useRef(done);
  const pausedAtRef = useRef(pausedAt);
  startedRef.current = started;
  runningRef.current = running;
  doneRef.current = done;
  pausedAtRef.current = pausedAt;
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onStart = () => {
      if (startedRef.current) return;
      const newDeadline = Date.now() + seconds * 1000;
      setDeadline(newDeadline);
      setPausedAt(null);
      setDone(false);
      setStarted(true);
      window.dispatchEvent(new CustomEvent("cookmode:timer-started"));
      try { onStart?.(); } catch {}
      try { onTimerStart?.(newDeadline, seconds); } catch {}
    };
    const onPause = () => {
      if (doneRef.current || !startedRef.current) return;
      if (runningRef.current) {
        const now = Date.now();
        setPausedAt(now);
        try { onTimerPause?.(Math.max(0, deadline - now)); } catch {}
      } else {
        const newDeadline = deadline + (Date.now() - pausedAtRef.current);
        setDeadline(newDeadline);
        setPausedAt(null);
        try { onTimerResume?.(newDeadline); } catch {}
      }
    };
    const onReset = () => {
      setStarted(false);
      setDeadline(Date.now() + seconds * 1000);
      setPausedAt(null);
      setDone(false);
      // Voice-command reset uses the same semantic as the button —
      // signal reset, NOT end.
      try { onTimerReset?.(); } catch {}
    };
    window.addEventListener("cookmode:request-timer-start", onStart);
    window.addEventListener("cookmode:request-timer-pause", onPause);
    window.addEventListener("cookmode:request-timer-reset", onReset);
    return () => {
      window.removeEventListener("cookmode:request-timer-start", onStart);
      window.removeEventListener("cookmode:request-timer-pause", onPause);
      window.removeEventListener("cookmode:request-timer-reset", onReset);
    };
    // Intentionally minimal deps — handlers read live state via
    // refs so we don't re-bind on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  // Tick the render clock. 500ms is plenty for a 1s-precision display
  // and smooths over Safari's coarser throttle on backgrounded tabs
  // (Safari's minimum throttle is ~1s, so 500ms gives us two shots at
  // a one-second grid). No cleanup complexity; we just repaint.
  // Gated on `started` so we don't run the interval before the user
  // has tapped START — `remaining` is meaningless at that point.
  useEffect(() => {
    if (!started || !running || done) return undefined;
    const id = setInterval(() => setTick(t => t + 1), 500);
    return () => clearInterval(id);
  }, [started, running, done]);

  // Detect rollover to 0 on any render (including the first one after
  // unlock that snaps from 3:42 past the deadline). Guarded by `done`
  // so onDone fires exactly once — idempotent re-triggers would double
  // the chime + fire extra local notifications. Also gated on
  // `started` so the pre-start render (when remaining = seconds) doesn't
  // accidentally fire onDone immediately for short timers.
  useEffect(() => {
    if (!started) return;
    if (!done && running && remaining <= 0) {
      setDone(true);
      setPausedAt(Date.now());   // stop the interval
      onDone?.();
      try { onTimerEnd?.(); } catch {}
    }
  }, [started, remaining, running, done, onDone, onTimerEnd]);

  // Fire onWarning exactly once when remaining first crosses the
  // tiered threshold (see warningSecondsFor). 30s for a short sauté,
  // 5 min for a 90-min braise, 30 min for an overnight bake — the
  // warning lands at a duration the cook can actually act on.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (!started) return;
    if (warnedRef.current) return;
    if (done) return;
    const threshold = warningSecondsFor(seconds);
    if (threshold == null) return;                    // too short to warn
    if (remaining > 0 && remaining <= threshold) {
      warnedRef.current = true;
      try { onWarning?.(); } catch (e) { console.warn("[Timer] onWarning threw:", e); }
    }
  }, [started, remaining, done, seconds, onWarning]);

  // Pause / resume — preserve "time remaining" across the flip.
  const togglePause = () => {
    if (done) return;
    if (running) {
      const now = Date.now();
      setPausedAt(now);
      try { onTimerPause?.(Math.max(0, deadline - now)); }
      catch (e) { console.warn("[Timer] onTimerPause threw:", e); }
    } else {
      // Shift the deadline forward by however long we were paused.
      const newDeadline = deadline + (Date.now() - pausedAt);
      setDeadline(newDeadline);
      setPausedAt(null);
      try { onTimerResume?.(newDeadline); }
      catch (e) { console.warn("[Timer] onTimerResume threw:", e); }
    }
  };

  // Also recheck remaining whenever the page becomes visible again —
  // covers the lock-screen case where `setInterval` got suspended for
  // N seconds; we want to repaint immediately on unlock, not wait for
  // the next 500ms tick.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVis = () => { if (document.visibilityState === "visible") setTick(t => t + 1); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  const mins=Math.floor(remaining/60), secs=remaining%60;
  const pct = seconds > 0
    ? Math.max(0, Math.min(100, (remaining / seconds) * 100))
    : 0;
  const showReset = started && (done || remaining !== seconds);
  const pulsing = !done && running && remaining > 0 && remaining <= 10;
  // Continuous green → yellow → red gradient over remaining %. HSL
  // hue 120° = green, 60° = yellow, 0° = red. Sliding linearly across
  // pct gives a smooth transition rather than the old discrete
  // amber/rose flip — the cook can read the color alone to gauge
  // time-left without parsing the digits. Done flips to a clean
  // leaf-green tick; pre-start sits at full green (100%).
  const hue = Math.max(0, Math.min(120, (pct / 100) * 120));
  const gradientColor = `hsl(${hue}, 62%, 42%)`;
  const barColor = done
    ? COLOR.leaf
    : !started
      ? gradientColor
      : running
        ? gradientColor
        : `hsl(${hue}, 35%, 55%)`;   // paused: same hue, desaturated
  const digitColor = done
    ? COLOR.leaf
    : !started
      ? gradientColor
      : running
        ? gradientColor
        : `hsl(${hue}, 38%, 38%)`;   // paused: same hue, muted
  const display = done ? "✓" : `${mins}:${String(secs).padStart(2,"0")}`;
  // Whole-block accent: border + faint background tint follow the
  // same hue the digits/bar use, so the cook reads "this is the
  // timer for this step, and it's currently <state>" from the card
  // itself, not just the bar. Done renders in solid leaf.
  const blockBorder = done ? COLOR.leaf : `hsl(${hue}, 55%, 48%)`;
  const blockBackground = done ? COLOR.leafDeep : `hsla(${hue}, 60%, 50%, 0.06)`;
  return (
    <div style={{
      marginTop: 16,
      display: "flex", flexDirection: "column", gap: 10,
      padding: "12px 14px",
      background: blockBackground,
      border: `1px solid ${blockBorder}`,
      borderRadius: 12,
      transition: "border-color 0.5s linear, background 0.5s linear",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 24, fontWeight: 700,
          color: digitColor,
          letterSpacing: "0.04em", minWidth: 78,
          animation: pulsing ? "cookTimerPulse 1s ease-in-out infinite" : "none",
          transition: "color 0.5s linear",
        }}>
          {display}
        </div>
        <div style={{ display: "flex", gap: 6, flex: 1 }}>
          {!started && (
            <button type="button" onClick={startTimer} style={timerActionBtnStyle(true, "teal")}>
              ▶ START
            </button>
          )}
          {started && !done && (
            <button
              onClick={togglePause}
              style={{
                ...timerActionBtnStyle(true, running ? "orange" : "teal"),
                // Override PAUSE bg to follow the gradient hue so the
                // button reads as part of the timer block, not a flat
                // orange punch-out. RESUME stays teal — it's a "go"
                // signal and benefits from a stable color.
                ...(running ? { background: gradientColor } : null),
              }}
            >
              {running ? "‖ PAUSE" : "▶ RESUME"}
            </button>
          )}
          {done && (
            <button
              type="button"
              onClick={restart}
              style={{
                ...timerActionBtnStyle(false),
                color: COLOR.leaf,
                borderColor: COLOR.leaf,
              }}
            >
              ↻ RESTART
            </button>
          )}
          {showReset && !done && (
            <button onClick={reset} style={timerActionBtnStyle(false)}>↻ RESET</button>
          )}
          {/* MARK DONE — failsafe override for any timer state pre-done.
              Always available so a wedged / stale-hydrated timer never
              traps the cook. Subdued tone so it's discoverable but
              doesn't compete with the primary START/PAUSE row. */}
          {!done && (
            <button
              type="button"
              onClick={markDone}
              title="Mark this timer done — useful if the timer is stuck or you're past it"
              aria-label="Mark timer done"
              style={{
                ...timerActionBtnStyle(false),
                color: COLOR.leaf,
                borderColor: COLOR.leaf,
                fontSize: 10,
                letterSpacing: "0.12em",
              }}
            >
              ✓ DONE
            </button>
          )}
        </div>
      </div>
      {!done && (
        <div
          aria-hidden="true"
          style={{
            position: "relative",
            width: "100%",
            height: 4,
            background: "rgba(60,40,18,0.10)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <div style={{
            position: "absolute",
            left: 0, top: 0, bottom: 0,
            width: `${pct}%`,
            background: barColor,
            transition: "width 0.5s linear, background 0.5s linear",
          }} />
        </div>
      )}
      <style>{`@keyframes cookTimerPulse{0%,100%{opacity:1}50%{opacity:0.55}}`}</style>
    </div>
  );
}

// Multi-stage countdown for steps that carry a `timers[]` array.
// Renders one row per sub-stage: each starts as PENDING, becomes ACTIVE
// (with the live Timer ring + pause/reset controls) when its turn comes,
// and flips to DONE on rollover. Auto-advances; fires `onAllDone` after
// the final stage completes.
//
// "2 min sear, then 8 min rest" stops being a single 10-min timer that
// misses the flip cue at the 2-min mark. Each stage gets its own
// countdown + chime; the cook hears the bell exactly when each phase
// ends.
export function MultiStepTimer({ timers, onAllDone, onStageDone }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const stages = Array.isArray(timers) ? timers : [];

  // Reset when the timer VALUES change (different step, new durations
  // post-calibration). Was reference equality (prevTimersRef.current
  // !== timers), which reset the active stage on every parent re-
  // render that produced a fresh `timers` array — even when the
  // values were identical. Now we hash a stable key off each stage's
  // (id || label, seconds) so the reset only fires when something
  // ABOUT THE TIMERS actually changed. Keying the component on
  // step.id at the call site still handles the cross-step jump; this
  // guard is the value-aware belt to that suspenders.
  const timersKey = (Array.isArray(timers) ? timers : [])
    .map((t) => `${t?.id || t?.label || ""}|${Number(t?.seconds) || 0}`)
    .join("·");
  const prevTimersKeyRef = useRef(timersKey);
  useEffect(() => {
    if (prevTimersKeyRef.current !== timersKey) {
      prevTimersKeyRef.current = timersKey;
      setActiveIndex(0);
    }
  }, [timersKey]);

  const handleStageDone = () => {
    onStageDone?.(activeIndex, stages[activeIndex]);
    if (activeIndex >= stages.length - 1) {
      onAllDone?.();
      return;
    }
    setActiveIndex(i => i + 1);
  };

  return (
    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      {stages.map((stage, i) => {
        const status = i < activeIndex ? "done" : i === activeIndex ? "active" : "pending";
        const seconds = Number(stage?.seconds) > 0 ? Number(stage.seconds) : 0;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        const totalLabel = mins > 0 && secs > 0
          ? `${mins}m ${secs}s`
          : mins > 0 ? `${mins}m` : `${secs}s`;
        const fg =
          status === "done"   ? COLOR.leaf
          : status === "active" ? COLOR.amber
          : "#9a9a9a";
        const dotBg =
          status === "done"   ? COLOR.leaf
          : status === "active" ? COLOR.amber
          : "transparent";
        return (
          <div key={i} style={{
            display: "flex", flexDirection: "column", gap: 6,
            padding: "10px 12px",
            background: status === "active" ? "rgba(218,165,32,0.06)" : "transparent",
            border: `1px solid ${status === "active" ? COLOR.amber : COLOR.border}`,
            borderRadius: 10,
            opacity: status === "pending" ? 0.55 : 1,
            transition: "all 0.25s",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 16, height: 16, borderRadius: "50%",
                border: `2px solid ${fg}`, background: dotBg,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: COLOR.soil, fontSize: 10, fontWeight: 700,
                flexShrink: 0,
              }}>
                {status === "done" ? "✓" : ""}
              </div>
              <div style={{
                fontFamily: FONT.mono, fontSize: 11, color: fg,
                letterSpacing: "0.08em", textTransform: "uppercase",
                flex: 1,
              }}>
                {stage?.label || `Stage ${i + 1}`}
              </div>
              <div style={{
                fontFamily: FONT.mono, fontSize: 10, color: fg,
                letterSpacing: "0.06em",
              }}>
                {totalLabel}
              </div>
            </div>
            {status === "active" && seconds > 0 && (
              <Timer
                key={`stage-${i}`}
                seconds={seconds}
                onDone={handleStageDone}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// TimerStack — N independent labeled countdowns the cook starts on
// demand. Replaces MultiStepTimer's sequential auto-cascade for steps
// that emit `step.timers: [{label, seconds}]`. Each timer:
//   • starts in PENDING (manual START required by default)
//   • flips to RUNNING when started → live wall-clock countdown
//   • can be PAUSED / RESUMED / RESET independently
//   • flips to DONE on rollover, fires its own chime + push
//
// Multiple timers can run in PARALLEL. The cook can have "mix" and
// "knead" both ticking if the recipe calls for that overlap.
//
// Per-step "Auto-cascade" toggle at the top. When ON:
//   • The first PENDING timer auto-starts on step entry
//   • Each subsequent timer auto-starts when its predecessor finishes
//   • User can still tap START manually to skip ahead
// When OFF (default): every timer waits for a manual START tap.
//
// Push notifications: scheduled at first START tap (not at mount), so
// "I forgot to set the timer" doesn't ring at the wrong time. Each
// timer's push fires at its independent deadline.
export function TimerStack({
  timers, onAllDone, onStageDone, onStageStart, onStageReset, onStageWarning,
  // Resume hand-off — when CookMode reopens on an in-flight stage
  // (banner tap mid-cook), App threads the live stage index + the
  // wall-clock deadline through. We hydrate that stage to "running"
  // and mark prior stages as "done" so sequential gating math holds
  // and the displayed countdown matches the banner / DB notification.
  resumeStageIdx = -1,
  resumeEndsAt = null,
  // Self-rehydration hand-off — when present, TimerStack queries
  // cook_session_steps on mount for every stage row keyed off
  // <parentStepId>-s<N> and seeds initial statuses + resumeDeadlines
  // from the DB. Without this the in-memory stage state is lost
  // every time the user navigates off the step (key changes →
  // remount), so a step with stages 0+1 already done would re-mount
  // showing all stages "pending" and the user couldn't pick up at
  // stage 2.
  cookSessionId = null,
  parentStepId = null,
  // Timer-state callbacks — fire from each stage's ManualTimer when
  // it starts / pauses / resumes / ends. CookMode uses these to
  // mirror state into the cook-session Live Activity. See Timer for
  // the canonical contract.
  onTimerStart, onTimerPause, onTimerResume, onTimerEnd, onTimerReset,
}) {
  const stages = Array.isArray(timers) ? timers : [];
  // Per-stage state: "pending" | "running" | "paused" | "done".
  // Re-keys when the timer values change so a different step starts
  // fresh; same value-based key as MultiStepTimer's reset guard so
  // unrelated parent re-renders don't snap stages back to pending.
  const stagesKey = stages.map((t) => `${t?.id || t?.label || ""}|${Number(t?.seconds) || 0}`).join("·");
  const buildInitialStatuses = () => {
    const valid = resumeStageIdx >= 0
      && resumeStageIdx < stages.length
      && resumeEndsAt
      && (() => {
        const ms = new Date(resumeEndsAt).getTime();
        return Number.isFinite(ms) && ms > Date.now();
      })();
    return stages.map((_, i) => {
      if (!valid) return "pending";
      if (i < resumeStageIdx) return "done";
      if (i === resumeStageIdx) return "running";
      return "pending";
    });
  };
  const [statuses, setStatuses] = useState(buildInitialStatuses);
  // Map of stageIdx → wall-clock ISO. Only populated for the resume
  // stage; cleared once that stage is reset/finished. ManualTimer
  // reads it via the `endsAt` prop on its initial mount.
  const [resumeDeadlines, setResumeDeadlines] = useState(() => {
    if (resumeStageIdx < 0 || !resumeEndsAt) return {};
    const ms = new Date(resumeEndsAt).getTime();
    if (!Number.isFinite(ms) || ms <= Date.now()) return {};
    return { [resumeStageIdx]: resumeEndsAt };
  });
  // Map of stageIdx → cook_session_steps.id, threaded down to
  // ManualTimer so its mount-time onTimerStart writes timer_state to
  // the RIGHT row even when activeStepRef in useCookTelemetry is
  // still racing with the just-fired startStep INSERT. Populated when
  // onStageStart awaits and resolves with the inserted row's id.
  // Cleared on reset and handleStageDone so the next fresh start
  // can re-insert without staleness.
  const [stepRowIds, setStepRowIds] = useState({});
  const prevKeyRef = useRef(stagesKey);
  useEffect(() => {
    if (prevKeyRef.current !== stagesKey) {
      prevKeyRef.current = stagesKey;
      setStatuses(stages.map(() => "pending"));
      setResumeDeadlines({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagesKey]);

  // Self-rehydration: on mount (and whenever cookSessionId/parentStepId
  // changes), pull every cook_session_steps row whose step_id matches
  // <parentStepId>-s<N> and seed statuses + resumeDeadlines from each.
  // This is what restores stage progress when the user navigates AWAY
  // from a multi-stage step and then comes back — without it, stages
  // 0/1 already done would reappear as pending and the user couldn't
  // pick up at stage 2.
  //
  // Status mapping per row:
  //   • finished_at set                       → done
  //   • timer_state.ended                     → done
  //   • timer_state.endsAt in past            → done
  //   • timer_state.endsAt future + isPaused  → paused (carry endsAt)
  //   • timer_state.endsAt future + !isPaused → running (carry endsAt)
  //   • no row                                → pending (default)
  useEffect(() => {
    if (!cookSessionId || !parentStepId) return undefined;
    let cancelled = false;
    (async () => {
      const baseId = String(parentStepId);
      const { data, error } = await supabase
        .from("cook_session_steps")
        .select("step_id, finished_at, timer_state")
        .eq("cook_session_id", cookSessionId)
        .or(`step_id.eq.${baseId},step_id.like.${baseId}-s*`);
      if (cancelled) return;
      if (error) {
        console.warn("[TimerStack] hydrate failed:", error.message);
        return;
      }
      if (!Array.isArray(data) || data.length === 0) return;
      const nowMs = Date.now();
      const nextStatuses = stages.map(() => "pending");
      const nextDeadlines = {};
      for (const r of data) {
        const m = String(r.step_id || "").match(/-s(\d+)$/);
        const stageIdx = m ? Number(m[1]) : 0;
        if (!Number.isInteger(stageIdx) || stageIdx < 0 || stageIdx >= stages.length) continue;
        if (r.finished_at) { nextStatuses[stageIdx] = "done"; continue; }
        const ts = r.timer_state || null;
        if (ts?.ended) { nextStatuses[stageIdx] = "done"; continue; }
        if (ts?.endsAt) {
          const endsMs = new Date(ts.endsAt).getTime();
          if (Number.isFinite(endsMs)) {
            if (endsMs <= nowMs) {
              nextStatuses[stageIdx] = "done";
            } else if (ts.isPaused) {
              nextStatuses[stageIdx] = "paused";
              nextDeadlines[stageIdx] = ts.endsAt;
            } else {
              nextStatuses[stageIdx] = "running";
              nextDeadlines[stageIdx] = ts.endsAt;
            }
          }
        }
        // No timer_state, no finished_at = row exists but never started;
        // leave pending so the user can tap START fresh.
      }
      console.log("[TimerStack:hydrate] parentStepId=" + baseId
        + " statuses=" + JSON.stringify(nextStatuses)
        + " deadlines=" + JSON.stringify(nextDeadlines));
      setStatuses(nextStatuses);
      setResumeDeadlines(nextDeadlines);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookSessionId, parentStepId]);

  // Sequential gating: a stage is unlockable only when every prior
  // stage is "done". Two stages can never run in parallel — the cook
  // works one timer at a time, the next becomes startable only after
  // the previous rings out.
  const isUnlocked = (statusList, i) => {
    for (let j = 0; j < i; j += 1) {
      if (statusList[j] !== "done") return false;
    }
    return true;
  };

  // Voice-command bridge for stacked timers — same window events
  // the single Timer listens for. Semantics for stacks:
  //   START  → start the first pending/paused stage (auto-advances
  //            through the cook one stage at a time).
  //   PAUSE  → pause whatever's currently running.
  //   RESET  → reset the currently running or paused stage back
  //            to pending.
  const statusesRef = useRef(statuses);
  statusesRef.current = statuses;
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onStart = () => {
      const list = statusesRef.current;
      for (let i = 0; i < list.length; i += 1) {
        if (list[i] === "pending" || list[i] === "paused") {
          startStage(i);
          return;
        }
      }
    };
    const onPause = () => {
      const list = statusesRef.current;
      for (let i = 0; i < list.length; i += 1) {
        if (list[i] === "running") { pauseStage(i); return; }
      }
    };
    const onReset = () => {
      const list = statusesRef.current;
      for (let i = 0; i < list.length; i += 1) {
        if (list[i] === "running" || list[i] === "paused") { resetStage(i); return; }
      }
    };
    window.addEventListener("cookmode:request-timer-start", onStart);
    window.addEventListener("cookmode:request-timer-pause", onPause);
    window.addEventListener("cookmode:request-timer-reset", onReset);
    return () => {
      window.removeEventListener("cookmode:request-timer-start", onStart);
      window.removeEventListener("cookmode:request-timer-pause", onPause);
      window.removeEventListener("cookmode:request-timer-reset", onReset);
    };
    // Handlers read live state via statusesRef; deps stay minimal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // startStage is async now: it awaits onStageStart's promise so the
  // inserted row's id can be threaded into stepRowIds[i] BEFORE
  // ManualTimer's mount-time onTimerStart fires. Without that order,
  // ManualTimer reads stepRowId=null and falls back to activeStepRef
  // which is still racing with the in-flight setActiveStep — the
  // bug that wrote step 2's deadline onto step 1's row.
  const startStage = async (i) => {
    const prev = statusesRef.current;
    if (prev[i] !== "pending" && prev[i] !== "paused") return;
    // No `isUnlocked` gate — stages within a TimerStack are
    // independently startable. The cook can run side-1 and side-2
    // sears in parallel, set a long bake going while a 5-min rest
    // happens, etc. Each stage owns its own cook_session_steps row,
    // so concurrent `timer_state` writes don't collide. The helper
    // is kept defined above for a future opt-in `sequential: true`
    // recipe flag if a chemistry-driven cook ever needs ordering.
    const wasPending = prev[i] === "pending";
    setStatuses((p) => {
      if (p[i] !== "pending" && p[i] !== "paused") return p;
      const next = [...p]; next[i] = "running"; return next;
    });
    // Fire the start hook ONLY on a fresh pending → running flip.
    // Resume from paused doesn't re-pin telemetry (push deadline
    // already in the DB) — same pattern the single Timer path uses.
    if (!wasPending) return;
    // Notify the per-step TTS narration to pause itself so
    // Chadwick and the timer voiceline don't talk over each
    // other. The single Timer path dispatches the same event in
    // its startTimer; mirror it here so multi-stage timers get
    // the same courtesy. Fired BEFORE onStageStart, which plays
    // the voiceline.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cookmode:timer-started"));
    }
    try {
      const rowId = await onStageStart?.(i, stages[i]);
      if (rowId) {
        setStepRowIds((prev) => ({ ...prev, [i]: rowId }));
      }
    } catch (e) {
      console.warn("[TimerStack] onStageStart threw:", e);
    }
  };
  // Restart a completed stage — the user finished the timer but wants
  // to run it again (extra minute on the sear, repeat a rest, etc.).
  // Drops any stale resume deadline, flips status from "done" →
  // "running" via a fresh pending step so the ManualTimer mounts
  // clean with seconds-from-now, then fires onStageStart so iOS
  // Live Activity + voicelines re-fire just like a fresh start.
  const restartStage = async (i) => {
    setResumeDeadlines((prev) => {
      if (!prev[i]) return prev;
      const next = { ...prev };
      delete next[i];
      return next;
    });
    // Clear stale row id from the previous run so ManualTimer waits
    // for the fresh INSERT's id before firing onTimerStart.
    setStepRowIds((prev) => {
      if (!prev[i]) return prev;
      const next = { ...prev };
      delete next[i];
      return next;
    });
    setStatuses((prev) => {
      if (prev[i] !== "done") return prev;
      const next = [...prev]; next[i] = "running";
      return next;
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cookmode:timer-started"));
    }
    try {
      const rowId = await onStageStart?.(i, stages[i]);
      if (rowId) {
        setStepRowIds((prev) => ({ ...prev, [i]: rowId }));
      }
    } catch (e) {
      console.warn("[TimerStack] onStageStart (restart) threw:", e);
    }
  };
  const pauseStage = (i) => setStatuses((prev) => {
    if (prev[i] !== "running") return prev;
    const next = [...prev]; next[i] = "paused"; return next;
  });
  const resetStage = (i) => {
    // Drop any resume-hydrated deadline first so a future re-start
    // begins with a fresh seconds-from-now countdown, not the stale
    // ISO that's already past.
    setResumeDeadlines((prev) => {
      if (!(i in prev)) return prev;
      const next = { ...prev }; delete next[i]; return next;
    });
    // Drop the stale stepRowId so the next startStage waits for the
    // new INSERT's id rather than writing to the row we just reset.
    setStepRowIds((prev) => {
      if (!(i in prev)) return prev;
      const next = { ...prev }; delete next[i]; return next;
    });
    setStatuses((prev) => {
      const next = [...prev]; next[i] = "pending"; return next;
    });
    // Tell the parent so it can cancel the pending push notification
    // and finish the open telemetry row — without this the cook
    // banner keeps ticking down a deadline the user just discarded.
    try { onStageReset?.(i, stages[i]); } catch (e) { console.warn("[TimerStack] onStageReset threw:", e); }
  };

  const handleStageDone = (i) => {
    // Drop the resume deadline once the stage finishes; subsequent
    // re-starts (after a manual reset) get a fresh seconds-from-now.
    setResumeDeadlines((prev) => {
      if (!(i in prev)) return prev;
      const next = { ...prev }; delete next[i]; return next;
    });
    // Drop the stepRowId — the row this stage was writing to is now
    // logically "done"; a restartStage will mint a new row.
    setStepRowIds((prev) => {
      if (!(i in prev)) return prev;
      const next = { ...prev }; delete next[i]; return next;
    });
    setStatuses((prev) => {
      const next = [...prev]; next[i] = "done"; return next;
    });
    onStageDone?.(i, stages[i]);
    if (statuses.every((s, idx) => idx === i || s === "done")) {
      onAllDone?.();
    }
  };

  return (
    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      {stages.map((stage, i) => {
        const seconds = Number(stage?.seconds) > 0 ? Number(stage.seconds) : 0;
        const status = statuses[i] || "pending";
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        const totalLabel = mins > 0 && secs > 0 ? `${mins}m ${secs}s`
          : mins > 0 ? `${mins}m` : `${secs}s`;
        const fg = status === "done" ? COLOR.leaf
          : status === "running" || status === "paused" ? COLOR.amber
          : "#9a9a9a";
        // When a stage is running/paused the inner ManualTimer renders
        // its OWN gradient-bordered card (border color tracks remaining
        // pct). Drop the outer wrapper's border in that state so we
        // don't double-nest two bordered boxes; the stage label header
        // still gets layout padding, just no surrounding frame.
        // Pending/done stages keep the original neutral frame so the
        // row reads as a discrete card.
        const isLive = status === "running" || status === "paused";
        return (
          <div key={i} style={{
            display: "flex", flexDirection: "column", gap: 8,
            padding: "10px 12px",
            background: "transparent",
            border: isLive ? "none" : `1px solid ${COLOR.border}`,
            borderRadius: 10,
            opacity: status === "done" ? 0.65 : 1,
            transition: "all 0.25s",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 16, height: 16, borderRadius: "50%",
                border: `2px solid ${fg}`,
                background: status === "done" ? COLOR.leaf : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: COLOR.soil, fontSize: 10, fontWeight: 700,
                flexShrink: 0,
              }}>
                {status === "done" ? "✓" : ""}
              </div>
              <div style={{
                fontFamily: FONT.mono, fontSize: 11, color: fg,
                letterSpacing: "0.08em", textTransform: "uppercase",
                flex: 1,
              }}>
                {stage?.label || `Stage ${i + 1}`}
              </div>
              <div style={{
                fontFamily: FONT.mono, fontSize: 10, color: fg,
                letterSpacing: "0.06em",
              }}>
                {totalLabel}
              </div>
            </div>
            {status === "pending" && (
              <button
                type="button"
                onClick={() => startStage(i)}
                style={timerStartBtnStyle}
              >
                ▶ START {stage?.label || `stage ${i + 1}`}
              </button>
            )}
            {status === "done" && (
              <button
                type="button"
                onClick={() => restartStage(i)}
                style={{
                  ...timerStartBtnStyle,
                  background: "transparent",
                  border: `1px solid ${COLOR.leaf}`,
                  color: COLOR.leaf,
                }}
              >
                ↻ Restart {stage?.label || `stage ${i + 1}`}
              </button>
            )}
            {(status === "running" || status === "paused") && seconds > 0 && (
              <ManualTimer
                key={`stage-${i}`}
                seconds={seconds}
                endsAt={resumeDeadlines[i] || null}
                paused={status === "paused"}
                onPause={() => pauseStage(i)}
                onResume={() => startStage(i)}
                onReset={() => resetStage(i)}
                onDone={() => handleStageDone(i)}
                onWarning={() => {
                  try { onStageWarning?.(i, stages[i]); }
                  catch (e) { console.warn("[TimerStack] onStageWarning threw:", e); }
                }}
                stageLabel={stage?.label || `stage ${i + 1}`}
                // Explicit row id from the awaited startStep INSERT
                // (or self-hydration); ManualTimer threads this to
                // onTimerStart so markTimerStarted writes to the
                // right cook_session_steps row instead of relying on
                // activeStepRef (which races with setActiveStep).
                stepRowId={stepRowIds[i] || null}
                // Resume mounts (TimerStack hydrated this stage from
                // an existing DB row carrying endsAt) shouldn't fire
                // onTimerStart at all — the row already has the
                // timer_state we'd be re-writing.
                skipMountTimerStart={!!resumeDeadlines[i]}
                onTimerStart={onTimerStart}
                onTimerPause={onTimerPause}
                onTimerResume={onTimerResume}
                onTimerEnd={onTimerEnd}
                onTimerReset={onTimerReset}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ManualTimer — thin wrapper that pins a wall-clock deadline ONLY
// when user taps START (parent flipped status to "running"), supports
// pause/resume by capturing remaining time at pause moment, and fires
// onDone exactly once on rollover. Push notification is wired in the
// parent caller — this component is purely the in-app countdown.
export function ManualTimer({
  seconds, paused, onPause, onResume, onReset, onDone, onWarning, stageLabel,
  endsAt = null,
  // When true (set by TimerStack for stages it hydrated from the DB),
  // this mount is a RESUME — the cook_session_steps row already has
  // the timer_state we'd otherwise be re-writing. Suppressing the
  // mount-time onTimerStart prevents a stale ref from clobbering the
  // wrong row's timer_state during a remount race.
  skipMountTimerStart = false,
  // Concrete cook_session_steps.id for the row this stage represents.
  // Threaded down from TimerStack after onStageStart awaits the
  // startStep INSERT. ManualTimer waits for a non-null stepRowId
  // before firing onTimerStart so markTimerStarted writes to the
  // correct row regardless of activeStepRef's race state. Null on
  // resume mounts (which skip the write entirely) or until the
  // INSERT round-trip resolves on a fresh start.
  stepRowId = null,
  // Timer-state callbacks — fire alongside the existing onPause /
  // onResume / onReset / onDone so CookMode can mirror state into
  // the cook-session Live Activity. See Timer for shape parity.
  onTimerStart, onTimerPause, onTimerResume, onTimerEnd, onTimerReset,
}) {
  // Deadline captures NOW + seconds at first mount (= the "START"
  // moment, since parent only renders this when status flips to
  // running). Pause snapshots remaining; resume re-pins deadline =
  // NOW + remaining.
  //
  // Resume case: when the parent passes `endsAt` (a wall-clock ISO
  // already pinned in the DB by an earlier user-initiated start), use
  // it instead of starting fresh. This is what keeps a TimerStack
  // stage in lockstep with the cook banner across remounts — without
  // it, TimerStack reset to seconds-from-now while the banner kept
  // showing the original deadline.
  const [deadline, setDeadline] = useState(() => {
    if (endsAt) {
      const ms = new Date(endsAt).getTime();
      if (Number.isFinite(ms) && ms > Date.now()) return ms;
    }
    return Date.now() + seconds * 1000;
  });
  const [remainingAtPause, setRemainingAtPause] = useState(null);
  const [tick, setTick] = useState(0);
  const [done, setDone] = useState(false);

  // Announce the timer started. CookMode hydrates the cook-session
  // Live Activity's active-timer fields from this. The deadline at
  // first mount is either the resume endsAt or seconds-from-now —
  // both correct; CookMode wraps the wall-clock on its side.
  //
  // GATING — the effect is keyed on stepRowId, NOT empty deps, so it
  // fires when (and only when) the parent has resolved the row id
  // from the async startStep INSERT. This is what prevents the
  // "starting timer 2 fires timer 1" bug: previously the mount-time
  // onTimerStart raced ahead of setActiveStep and markTimerStarted
  // wrote to the prior step's row via a stale activeStepRef.
  //
  // Two short-circuits up top:
  //   • endsAt set → resume mount; row already carries the right
  //     timer_state, nothing to re-write.
  //   • skipMountTimerStart explicitly set by parent → same intent
  //     but driven by parent state (TimerStack passes it for any
  //     stage that hydrated from a DB row).
  //
  // NOTE: onTimerEnd is fired exclusively from the rollover branch
  // below (`remainingSec <= 0`). Previously this lived in the
  // unmount cleanup, which made every navigation-away event LOOK
  // like a timer completion to CookMode — TimerStack stages got
  // marked DONE on the bar the moment the user tapped NEXT STEP,
  // even with minutes still on the clock.
  const startFiredRef = useRef(false);
  useEffect(() => {
    if (endsAt || skipMountTimerStart) return;
    if (!stepRowId) return;
    if (startFiredRef.current) return;
    startFiredRef.current = true;
    try { onTimerStart?.(deadline, seconds, stageLabel, stepRowId); }
    catch (e) { console.warn("[ManualTimer] onTimerStart threw:", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepRowId]);

  // When `paused` flips on, snapshot remaining; when it flips off,
  // re-pin deadline using that remaining + now.
  const prevPausedRef = useRef(paused);
  useEffect(() => {
    if (prevPausedRef.current === paused) return;
    prevPausedRef.current = paused;
    if (paused) {
      const rem = Math.max(0, deadline - Date.now());
      setRemainingAtPause(rem);
      try { onTimerPause?.(rem); }
      catch (e) { console.warn("[ManualTimer] onTimerPause threw:", e); }
    } else if (remainingAtPause != null) {
      const newDeadline = Date.now() + remainingAtPause;
      setDeadline(newDeadline);
      setRemainingAtPause(null);
      try { onTimerResume?.(newDeadline); }
      catch (e) { console.warn("[ManualTimer] onTimerResume threw:", e); }
    }
  }, [paused, deadline, remainingAtPause, onTimerPause, onTimerResume]);

  const remainingMs = remainingAtPause != null ? remainingAtPause : Math.max(0, deadline - Date.now());
  const remainingSec = Math.ceil(remainingMs / 1000);

  useEffect(() => {
    if (done || paused) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [done, paused]);

  useEffect(() => {
    if (done) return;
    if (remainingSec <= 0) {
      setDone(true);
      try { playTimerChime(); } catch { /* noop */ }
      onDone?.();
      // Fire onTimerEnd HERE (the natural-rollover branch) so
      // CookMode marks the segment complete only when the timer
      // actually rang — never on a navigation-driven unmount.
      try { onTimerEnd?.(); }
      catch (e) { console.warn("[ManualTimer] onTimerEnd (rollover) threw:", e); }
    }
  }, [remainingSec, done, onDone, onTimerEnd]);

  // Tiered warning fire — same shape as the single Timer (see
  // warningSecondsFor at top of file). 30s on a 4-min sear, 5min
  // on a 90-min braise, etc. Stage timers used to skip warnings
  // entirely; they now play "thirty seconds" / "five minutes left"
  // voicelines just like the single Timer path.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (paused || done) return;
    if (warnedRef.current) return;
    const threshold = warningSecondsFor(seconds);
    if (threshold == null) return;
    if (remainingSec > 0 && remainingSec <= threshold) {
      warnedRef.current = true;
      try { onWarning?.(); } catch (e) { console.warn("[ManualTimer] onWarning threw:", e); }
    }
  }, [remainingSec, paused, done, seconds, onWarning]);

  const mm = Math.floor(remainingSec / 60);
  const ss = remainingSec % 60;
  const display = `${mm}:${String(ss).padStart(2, "0")}`;

  // Restart — explicitly re-run a finished timer. Clears `done`,
  // pins a fresh deadline `nominal` seconds from now, and fires
  // onTimerStart so CookMode rewrites timer_state (clearing the
  // `ended` flag) and drops this segment out of completedTimer-
  // Indices on the bar. The user's mental model: timer rang →
  // DONE → tap Restart → step is active again, checkmark gone.
  const handleRestart = () => {
    try { stopTimerChime(); } catch { /* noop */ }
    const newDeadline = Date.now() + totalSecRef.current * 1000;
    setDone(false);
    setRemainingAtPause(null);
    setDeadline(newDeadline);
    warnedRef.current = false;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cookmode:timer-started"));
    }
    try { onTimerStart?.(newDeadline, totalSecRef.current, stageLabel); }
    catch (e) { console.warn("[ManualTimer] onTimerStart (restart) threw:", e); }
  };

  // Progress percentage. Captured-once total via useRef so the
  // denominator stays stable across pause/resume cycles (resume
  // re-pins `deadline` which would otherwise mess up a remainingSec /
  // (deadline - mountTime) calculation). When paused, remainingMs
  // freezes via the snapshot, so the bar holds its position
  // without explicit handling.
  const totalSecRef = useRef(seconds);
  const pct = totalSecRef.current > 0
    ? Math.max(0, Math.min(100, (remainingSec / totalSecRef.current) * 100))
    : 0;
  // Same green→yellow→red HSL gradient the single Timer uses, so
  // a TimerStack stage and a single-Timer step read identically.
  const hue = Math.max(0, Math.min(120, (pct / 100) * 120));
  const gradientColor = `hsl(${hue}, 62%, 42%)`;
  const barColor = paused ? `hsl(${hue}, 35%, 55%)` : gradientColor;
  const digitColor = paused ? `hsl(${hue}, 38%, 38%)` : gradientColor;
  const blockBorder = `hsl(${hue}, 55%, 48%)`;
  const blockBackground = `hsla(${hue}, 60%, 50%, 0.06)`;

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 8,
      padding: "10px 12px",
      background: blockBackground,
      border: `1px solid ${blockBorder}`,
      borderRadius: 12,
      transition: "border-color 0.5s linear, background 0.5s linear",
    }}>
      {/* Chunky progress bar — the dominant element on the card. Per
          user feedback: bar status is the most important thing.
          Track is neutral, fill anchored to the left edge so the bar's
          right edge recedes leftward over time. transition: "width
          0.5s linear" matches the 500ms tick cadence so motion is
          smooth instead of step-wise. Hidden when done so a 0%-width
          bar with no border doesn't render as a hairline. */}
      {!done && (
        <div
          aria-hidden="true"
          style={{
            position: "relative",
            width: "100%",
            height: 16,
            background: "rgba(60,40,18,0.10)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <div style={{
            position: "absolute",
            left: 0, top: 0, bottom: 0,
            width: `${pct}%`,
            background: barColor,
            transition: "width 0.5s linear, background 0.5s linear",
          }} />
        </div>
      )}
      {/* Timer + actions row — timer small in the lower-left so the
          bar is the visual anchor; pause/reset right-aligned. When
          DONE: the display swaps to "✓ DONE" in leaf-green and the
          pause/reset buttons collapse to a single Restart CTA. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 13, fontWeight: 600,
          color: done ? COLOR.leaf : digitColor, letterSpacing: "0.04em",
          fontVariantNumeric: "tabular-nums",
          transition: "color 0.5s linear",
          flex: "0 0 auto",
        }}>
          {done ? "✓ DONE" : display}
        </div>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
          {done ? (
            <button
              type="button"
              onClick={handleRestart}
              style={{
                ...timerActionBtnStyle(false),
                color: COLOR.leaf,
                borderColor: COLOR.leaf,
              }}
            >
              ↻ RESTART
            </button>
          ) : paused ? (
            <button type="button" onClick={onResume} style={timerActionBtnStyle(true, "teal")}>▶ RESUME</button>
          ) : (
            <button
              type="button"
              onClick={onPause}
              style={{
                ...timerActionBtnStyle(true, "orange"),
                background: gradientColor,
              }}
            >
              ‖ PAUSE
            </button>
          )}
          {!done && (
            <button type="button" onClick={onReset} style={timerActionBtnStyle(false)}>↻ RESET</button>
          )}
          {/* MARK DONE failsafe — same intent as the single Timer's
              ✓ DONE button. Fires the same path as natural rollover so
              the cook can manually advance a wedged / stale-hydrated
              stage. Idempotent via the local `done` flag. */}
          {!done && (
            <button
              type="button"
              onClick={() => {
                try { stopTimerChime(); } catch { /* noop */ }
                setDone(true);
                try { onDone?.(); }
                catch (e) { console.warn("[ManualTimer] onDone threw:", e); }
                try { onTimerEnd?.(); }
                catch (e) { console.warn("[ManualTimer] onTimerEnd threw:", e); }
              }}
              title="Mark this stage done — useful if the timer is stuck or you're past it"
              aria-label="Mark stage done"
              style={{
                ...timerActionBtnStyle(false),
                color: COLOR.leaf,
                borderColor: COLOR.leaf,
                fontSize: 10,
                letterSpacing: "0.12em",
              }}
            >
              ✓ DONE
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const timerStartBtnStyle = {
  alignSelf: "stretch",
  padding: "10px 14px",
  background: COLOR.amber,
  color: COLOR.night,
  border: "none",
  borderRadius: 8,
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
  letterSpacing: "0.10em", textTransform: "uppercase",
  cursor: "pointer",
  touchAction: "manipulation",
};

// `tone` colors filled buttons against the MCM palette so the timer
// row reads at a glance: teal = "go" (start/resume), burnt-orange =
// "pause", neutral ink for anything else. The unfilled (RESET) shape
// stays the same — outlined, muted, no semantic tone.
export function timerActionBtnStyle(filled, tone = "ink") {
  const TONE_BG = {
    ink:    COLOR.ink,
    teal:   COLOR.leaf,   // signature teal-green for resume / start
    orange: COLOR.rose,   // signature burnt-orange for pause
  };
  const bg = TONE_BG[tone] || COLOR.ink;
  return {
    flex: 1,
    padding: "8px 12px",
    background: filled ? bg : "transparent",
    color: filled ? "#FFFFFF" : COLOR.muted,
    border: filled ? "none" : `1px solid ${COLOR.border}`,
    borderRadius: 8,
    fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
    letterSpacing: "0.10em", textTransform: "uppercase",
    cursor: "pointer",
    touchAction: "manipulation",
  };
}
